/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  useGetResourcesTasks,
  useGetResource,
  useAssignTask,
  usePermissions,
  useTabHandler,
  useGetEventsForResource,
  useGetSchedules,
  useUpsertResource,
  useUpsertSchedules,
  useUpsertEvents,
  useGetQualifiedResources,
  useGetResourcesPaginated,
  useRescheduleTask,
  useCompleteExpiredTasks,
  useForceReassign,
  useShowLocationBasedEvents,
  useCompany,
} from '@dispatch/shared/hooks';
import { useNotification, useResourceDataGrid } from '@dispatch/web/ui';
import {
  EditEventForm,
  EditScheduleForm,
  HandleReassignProps,
  ITaskMessage,
  MultiSelectOption,
  Resource,
  ResourceForm,
  TaskAssignmentRequest,
  TaskRescheduleRequest,
} from '@dispatch/shared/types';
import { ResourcesDetails } from '@dispatch/web/features/resources';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ResourceEventHelper,
  ResourceFormHelper,
  ResourceScheduleHelper,
} from '@dispatch/shared';
import { useCurrentUser } from '@dispatch/web/auth';
import { useQueryClient } from 'react-query';
import React from 'react';
import { useNotificationMutationFactory } from '@dispatch/web/hooks';
import { addMinutes } from 'date-fns';
import { useValidateTaskErrors } from '../../tasks/utils/ErrorHandlers';
import { isTaskReassignable } from '@dispatch/shared/services';
import { isNull } from 'lodash';

const ResourceDetailsPage = ({
  navigateToOnClose = '/resources',
}: {
  navigateToOnClose?: string;
}) => {
  const { company, efficiencyOptions, featureFlags } = useCompany();
  const { currentTab, setTab } = useTabHandler();
  const { permissions } = usePermissions();
  const { tenantId } = useCurrentUser();
  const { showLocationBasedEvents } = useShowLocationBasedEvents(
    tenantId || ''
  );
  const { notifyError, notifySuccess } = useNotification();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReadOnly = !isNull(searchParams.get('readonly'))
    ? searchParams.get('readonly') === 'true'
      ? true
      : false
    : false;

  const queryClient = useQueryClient();
  const {
    status,
    data,
    refetch: refetchResource,
  } = useGetResource({
    id: params.resourceId || '',
    company,
  });

  const schedulesQuery = useGetSchedules({
    resourceId: params.resourceId || '',
  });

  const certsAndLicense = React.useMemo(
    () => company?.certificationAndLicense,
    [company]
  );

  const eventsQuery = useGetEventsForResource({
    resourceId: params.resourceId || '',
  });

  const onCompleteDispatch = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess(
        'Task dispatched successfully',
        'Task has been successfully dispatched'
      );
      queryClient.invalidateQueries({
        queryKey: ['schedule-data'],
        exact: false,
      });
    } else {
      notifyError('Error', 'Task was not successfully dispatched');
    }
  };

  const resourceId = params.resourceId || '';
  const tasks = useGetResourcesTasks(resourceId);

  const onCompleteSchedule = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Schedule Saved', 'Schedule has been successfully saved');
      schedulesQuery.refetch && schedulesQuery.refetch();
      queryClient.invalidateQueries(['schedules-data']);
    } else {
      notifyError('Error', 'Schedule was not saved');
    }
  };

  const onCompleteEvent = (response: { success: boolean; message: string }) => {
    if (response?.success) {
      notifySuccess('Event Saved', 'Event has been successfully saved');
      eventsQuery.refetch && eventsQuery.refetch();
      queryClient.invalidateQueries({
        queryKey: ['schedules-data'],
        exact: false,
      });
    } else {
      if (response.message === 'Conflict') {
        notifyError('Error', 'There was a scheduling conflict');
      } else {
        notifyError('Error', 'Event was not saved');
      }
    }
  };

  const onCompleteResource = (response: {
    success: boolean;
    message?: string;
  }) => {
    if (response?.success) {
      notifySuccess('Resource Saved', 'Resource has been successfully saved');
      queryClient.invalidateQueries({
        queryKey: ['schedule-data'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['resource-tasks-unassigned', params.resourceId],
        exact: false,
      });
      refetchResource && refetchResource();
    } else {
      notifyError('Resource was not saved', response.message);
    }
  };

  const onCompleteExpired = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess(
        'Tasks Completed',
        'Past tasks have been completed successfully'
      );
      // refetch tasks to update task statuses
      tasks[0].refetch && tasks[0].refetch();
    } else {
      notifyError('Error', 'Past tasks not Completed');
    }
  };

  const { mutate, status: dispatchStatus } = useAssignTask(onCompleteDispatch);

  //Only does insert for now
  const { upsertSchedules, upsertSchedulesStatus } =
    useUpsertSchedules(onCompleteSchedule);

  //Only does insert for now
  const { upsertEvents, upsertEventsStatus } = useUpsertEvents(onCompleteEvent);

  const { upsertResource, upsertResourceStatus } = useUpsertResource(
    params?.resourceId || '',
    onCompleteResource
  );

  const { completeExpiredMutate, completeExpiredStatus } =
    useCompleteExpiredTasks(onCompleteExpired);

  function handleSave({ selected }: { selected: TaskRescheduleRequest[] }) {
    const postBody: TaskAssignmentRequest[] = [];
    selected?.forEach((selected) => {
      selected.isPrecise = false;
      const { id, ...rest } = selected;
      postBody.push({
        data: {
          ...rest,
        },
        taskId: id || '',
      });
    });

    mutate(postBody);
  }

  function completePastTasks() {
    completeExpiredMutate(resourceId);
  }

  const onForceReassign = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess(
        'Tasks Reassigned Successfully',
        'Tasks have been successfully reassigned'
      );
      // refetch tasks to update task statuses
      tasks[0].refetch && tasks[0].refetch();
    } else {
      notifyError('Error', 'Tasks were not reassigned');
    }
  };

  const handleForceReassign = React.useCallback(
    (
      values: { resource: MultiSelectOption },
      selectedTasks?: ITaskMessage[]
    ) => {
      const taskIds = selectedTasks?.reduce((result: string[], task: any) => {
        if (isTaskReassignable(task)) {
          result.push(task.id as string);
        }
        return result;
      }, []);
      if (!values || !taskIds || taskIds?.length <= 0) {
        notifyError('Error', 'Unable to assign tasks');
      } else {
        return reassignTasks({
          taskIds: taskIds,
          resourceId: values.resource.value as string,
        });
      }
    },
    [tasks]
  );

  const { mutate: reassignTasks } = useForceReassign(onForceReassign);

  const handleEventSave = (values: EditEventForm) => {
    if (!values || !params?.resourceId) {
      notifyError(
        'Error',
        'Unable to Save Event' // generic error
      );
    } else {
      const events = ResourceEventHelper(
        values,
        params.resourceId,
        tenantId || ''
      );

      upsertEvents(events);
    }
  };

  const handleScheduleSave = (values: EditScheduleForm) => {
    if (!values || !params?.resourceId) {
      notifyError(
        'Error',
        'Unable to Save Schedule' // generic error
      );
    } else {
      const result = ResourceScheduleHelper(
        values,
        params.resourceId,
        schedulesQuery?.data || [],
        tenantId || ''
      );

      if (result.hasDuplicate) {
        const dates = result?.schedules?.map((schedule) =>
          new Date(schedule.workingDayStartTime).toDateString()
        );

        if (dates.length > 4) {
          dates.splice(3);
          dates.push('...');
        }

        notifyError(
          'Duplicate Schedule',
          `One or more days already have schedules, please revise the following dates: ${dates}`,
          { autoHideDuration: 4000 }
        );
      } else {
        upsertSchedules(result.schedules);
      }
    }
  };

  const handleResourceSave = (values: ResourceForm) => {
    if (!values || !data) {
      notifyError(
        'Error',
        'Unable to Save Resource' // generic error as this neither of these values should be empty
      );
    } else {
      const resource = ResourceFormHelper(
        values,
        data,
        tenantId || '',
        featureFlags?.fieldCapacityByDayEnabled ?? false
      );

      upsertResource(resource);
    }
  };

  const hasActiveTasks = () => {
    return tasks?.[0].data?.some(
      (task: any) =>
        task.targetQueue !== 'egress' && task.targetQueue !== 'cancelled_q'
    );
  };

  const handleActivateOrDeactivate = (isActive: boolean) => {
    if (!data) {
      notifyError(
        'Error',
        'Unable to Save Resource' // generic error as this neither of these values should be empty
      );
    } else if (!isActive && hasActiveTasks()) {
      notifyError(
        'Error',
        'Resource cannot be deactivated because they have active tasks.'
      );
    } else {
      data.isActive = isActive;
      upsertResource(data);
    }
  };

  const onStandbySave = (isStandby: boolean) => {
    if (!data) {
      notifyError(
        'Error',
        'Unable to Save Resource' // generic error as this neither of these values should be empty
      );
    } else {
      data.isStandby = isStandby;
      upsertResource(data);
    }
  };

  const resourceMeta = useResourceDataGrid();

  const resources = useGetResourcesPaginated({
    filters: resourceMeta?.resourcesFilters,
    enabled: !resourceMeta?.showOnlyQualified,
  });

  const qualifiedResources = useGetQualifiedResources(
    resourceMeta?.taskToAssign?.id || '',
    resourceMeta?.showOnlyQualified
  );

  const resourcesData = React.useMemo(() => {
    if (!resourceMeta?.showOnlyQualified) {
      const resourcesWithoutCurrentResource = resources?.data?.filter(
        (resource) => resource.integrationGUID !== params?.resourceId
      );
      return {
        ...resources,
        data: resourcesWithoutCurrentResource,
      };
    }
    return qualifiedResources;
  }, [qualifiedResources, resources, resourceMeta?.taskToAssign]);

  const qualifiedStatus = React.useMemo(() => {
    if (!resourceMeta?.showOnlyQualified) {
      return resources.status;
    }
    return qualifiedResources.status;
  }, [qualifiedResources.status, resources.status]);

  const notifications = useNotificationMutationFactory('Task', 'update');
  const { handleError } = useValidateTaskErrors(notifyError);

  const onRescheduleError = (errorMessage: {
    status: number;
    message: string;
  }) => {
    handleError(errorMessage);
  };
  const rescheduleMutation = useRescheduleTask(() => {
    notifications.notifySuccess();
    resourceMeta?.setTaskToAssign(undefined);
  }, onRescheduleError);

  const handleReassign = (data: HandleReassignProps) => {
    if (data.forceReassign) {
      reassignTasks({
        taskIds: [data.task?.id || ''],
        resourceId: data.resourceId,
        reasonCode: data.reasonCode,
      });
    } else {
      const request: TaskRescheduleRequest = {
        resourceId: data.resourceId,
        reasonCode: data.reasonCode,
        appointmentStart: data?.task?.appointment || '',
        appointmentEnd: '',
        isPrecise: false,
      };

      if (data?.task?.appointment) {
        request.appointmentEnd = addMinutes(
          new Date(data?.task?.appointment),
          data?.task?.appointmentDurationInMinutes
        ).toJSON();
      }

      rescheduleMutation.mutate({
        taskId: data?.task?.id || '',
        request,
      });
    }
  };

  return (
    <ResourcesDetails
      qualifiedState={{
        showOnlyQualified: resourceMeta?.showOnlyQualified,
        setShowOnlyQualified: resourceMeta?.setShowOnlyQualified,
      }}
      isReadOnly={isReadOnly}
      qualifiedStatus={qualifiedStatus}
      resourcesData={resourcesData}
      onCompletePastTasks={completePastTasks}
      onTaskSave={handleSave}
      onResourceSave={handleResourceSave}
      onEventSave={handleEventSave}
      onScheduleSave={handleScheduleSave}
      status={status}
      upsertScheduleStatus={upsertSchedulesStatus}
      upsertEventStatus={upsertEventsStatus}
      upsertResourceStatus={upsertResourceStatus}
      upsertResource={upsertResource}
      dispatchStatus={dispatchStatus}
      completeTasksStatus={completeExpiredStatus}
      data={data || ({} as Resource)}
      tasks={{
        assigned: {
          data: (tasks && tasks?.[0].data) || [],
          loading: tasks && tasks?.[0].isLoading,
        },
        unassigned: {
          data: (tasks && tasks?.[1].data) || [],
          loading: tasks && tasks?.[1].isLoading,
        },
      }}
      permissions={permissions}
      currentTab={currentTab}
      certsAndLicense={certsAndLicense}
      setTab={setTab}
      onActivateOrDeactivate={handleActivateOrDeactivate}
      eventsQuery={eventsQuery}
      schedulesQuery={schedulesQuery}
      efficiencies={efficiencyOptions || []}
      navigateToOnClose={navigateToOnClose}
      resourceMeta={{
        ...resourceMeta,
        handleReassign,
      }}
      onStandbySave={onStandbySave}
      onForceReassign={handleForceReassign}
      showLocationBasedEvents={showLocationBasedEvents}
    />
  );
};

export default ResourceDetailsPage;
